import {formatUrl} from '@teemill/common/helpers';
import {BaseClass, Thumbnail} from '@teemill/common/classes';
import Blog from './blog';
import BlogTag from './blogTag';

export default class BlogPostSeries extends BaseClass {
  constructor({
    id = null,
    title = null,
    description = null,
    image = null,
    url = null,
    editUrl = null,
    fullUrl = null,
    urlTitle = null,
    division = null,
    updatedAt = null,
    published = false,

    tags = [],
    blogPosts = [],
    order = null,
  } = {}) {
    super();

    this.id = id;
    this.title = title;
    this.description = description;
    this.division = division;
    this.updatedAt = updatedAt;
    this.published = published;
    this.editUrl = editUrl;
    this.fullUrl = fullUrl;
    this.urlTitle = urlTitle;
    this.url = url;
    this.image = new Thumbnail(image);
    this.tags = tags;
    this.tags = BlogTag.map(tags);
    this.blogPosts = Blog.map(blogPosts);

    this.order = order || this.blogPosts.map(blogPost => blogPost.id);
  }

  /**
   * @name isNew
   * @description Is the blog series persisted to backend or not
   *
   * @returns {boolean}
   */
  get isNew() {
    return !this.id;
  }

  /**
   * @name postCount
   * @description The number of posts in this series
   *
   * @returns {Int}
   */
  get postCount() {
    return this.blogPosts.length;
  }

  /**
   * @name publishedPostCount
   * @description The number of posts in this series that are "published"
   *
   * @returns {Int}
   */
  get publishedPostCount() {
    return this.publishedPosts.length;
  }

  /**
   * @name has
   * @description Does the series contain this post
   *
   * @param {Blog} post
   *
   * @return {Boolean}
   */
  has(post) {
    return Boolean(
      this.blogPosts.find(seriesPosts => seriesPosts.id === post.id)
    );
  }

  /**
   * @name publishedPosts
   * @description Get all of the "published" posts for this series
   *
   * @returns {BlogPost[]}
   */
  get publishedPosts() {
    return this.blogPosts.filter(post => post.published);
  }

  /**
   * @name show
   * @description Find a blog post by code
   *
   * @param {String} urlTitle
   * @param {Number} division
   *
   * @returns {Promise}
   */
  static show(urlTitle, {division} = {}) {
    return $axios
      .get(formatUrl(`/omnis/v3/blog/series/show/${urlTitle}`), {
        params: {division},
      })
      .success(series => BlogPostSeries.map(series))
      .handle(404, () => null)
      .output();
  }

  /**
   * @name getImage
   * @description Get an appropriate image size for the series
   *
   * @param {String} size
   *
   * @returns {String}
   */
  getImage(size = null) {
    if (typeof this.image === 'string') {
      return this.image;
    }

    return this.image.size(size);
  }

  /**
   * @name getNextBlogPost
   * @description Try to find the next blog post for this series
   *
   * @param {Blog} blogPost
   *
   * @returns {Blog}
   */
  getNextBlogPost(blogPost) {
    const nextPost = this.order.findIndex(blog => blog === blogPost.id) + 1;

    return this.blogPosts.find(blog => blog.id === this.order[nextPost]);
  }

  /**
   * @name getPreviousBlogPost
   * @description Try to find the previous blog post for this series
   *
   * @param {Blog} blogPost
   *
   * @returns {Blog}
   */
  getPreviousBlogPost(blogPost) {
    const previousPost = this.order.findIndex(blog => blog === blogPost.id) - 1;

    return this.blogPosts.find(blog => blog.id === this.order[previousPost]);
  }

  /**
   * @name addBlogPost
   * @description Add a blog post to the series if not already included
   *
   * @param {Blog} blogPost
   *
   * @returns {BlogPostSeries}
   */
  addBlogPost(blogPost) {
    if (this.blogPosts.find(b => b.id === blogPost.id)) {
      return this;
    }

    this.blogPosts.push(blogPost);

    this.order = this.blogPosts.map(b => b.id);

    return this;
  }

  /**
   * @name removeBlogPost
   * @description Remove a blog post to the series if it exists
   *
   * @param {Blog} blogPost
   *
   * @returns {BlogPostSeries}
   */
  removeBlogPost(blogPost) {
    if (!this.blogPosts.find(b => b.id === blogPost.id)) {
      return this;
    }

    this.blogPosts = this.blogPosts.filter(b => b.id !== blogPost.id);

    this.order = this.blogPosts.map(b => b.id);

    return this;
  }

  /**
   * @name save
   * @description Persist this BlogPostSeries to the API
   *
   * @returns {Promise}
   */
  save() {
    return $axios
      .post(
        formatUrl(`/omnis/v3/blog/series/${this.isNew ? 'create' : 'update'}`),
        {
          id: this.id,
          title: this.title,
          url_title: this.urlTitle,
          description: this.description,
          image: typeof this.image === 'string' ? this.image : undefined,
          division: this.division,
          tags: this.tags.map(tag => tag.code),
          blogPosts: this.order,
          published: this.published,
        }
      )
      .success(series => {
        snackbar('Saved');

        return BlogPostSeries.map(series);
      })
      .validation()
      .handle(401, () => {
        snackbar.error('This action is not authorised');
      })
      .output();
  }

  /**
   * @name destroy
   * @description Attempt to delete this BlogPostSeries from the API
   *
   * @returns {Promise}
   */
  destroy() {
    return $axios
      .post(formatUrl('/omnis/v3/blog/series/destroy'), {
        id: this.id,
        division: this.division,
      })
      .success(() => {
        snackbar('Deleted');
      })
      .oops();
  }
}
